import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ScanLine } from 'lucide-react';
import { toast } from 'sonner';
import { Html5Qrcode } from 'html5-qrcode';
import { api } from '../lib/api';
import { useI18n } from '../context/I18nContext';
import UserAvatar from '../components/UserAvatar';
import AuraScore from '../components/AuraScore';
import { getLevelForAura } from '../lib/aura';
import { useAuth } from '../context/AuthContext';

/**
 * ⚔️ ESCANEAR CONTRINCANTE
 * Uses html5-qrcode with rear camera. Extracts the token from either a raw
 * hex string or from an /scan#{token} URL and hits /qr/u/{token}.
 */
export default function ScannerPage() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [scanned, setScanned] = useState(null);
  const [error, setError] = useState(null);
  const [challenging, setChallenging] = useState(false);
  const { user } = useAuth();
  const myBalance = Number(user?.aura_points || 0);
  const scannerRef = useRef(null);
  const mountedRef = useRef(true);

  const parseToken = useCallback((text) => {
    if (!text) return null;
    // Accept full URL or bare token
    const hashMatch = text.match(/#([a-f0-9]{16,64})/i);
    if (hashMatch) return hashMatch[1];
    const pathMatch = text.match(/\/qr\/u\/([a-f0-9]{16,64})/i);
    if (pathMatch) return pathMatch[1];
    if (/^[a-f0-9]{16,64}$/i.test(text.trim())) return text.trim();
    return null;
  }, []);

  const resolveToken = useCallback(async (token) => {
    try {
      const { data } = await api.get(`/qr/u/${token}`);
      setScanned({ ...data, token });
    } catch (err) {
      setError(err?.response?.data?.detail || t('scan.invalid'));
    }
  }, [t]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (scanned) return undefined;
    // Deep-link: /scan#TOKEN
    const hash = window.location.hash || '';
    const deepToken = parseToken(hash);
    if (deepToken) {
      window.history.replaceState(null, '', '/scan');
      resolveToken(deepToken);
      return undefined;
    }

    let cancelled = false;
    const el = document.getElementById('qr-region');
    if (!el) return undefined;
    const scanner = new Html5Qrcode('qr-region', { verbose: false });
    scannerRef.current = scanner;

    const onSuccess = async (text) => {
      const token = parseToken(text);
      if (!token) return;
      try { await scanner.stop(); await scanner.clear(); } catch {}
      if (!cancelled && mountedRef.current) resolveToken(token);
    };
    const onError = () => {};

    scanner
      .start({ facingMode: 'environment' }, { fps: 10, qrbox: 260 }, onSuccess, onError)
      .catch((e) => { setError(e?.message || t('scan.cameraDenied')); });

    return () => {
      cancelled = true;
      if (scannerRef.current) {
        try { scannerRef.current.stop().then(() => scannerRef.current.clear()).catch(() => {}); } catch {}
      }
    };
  }, [scanned, parseToken, resolveToken, t]);

  const [stake, setStake] = useState(100);

  const challenge = async () => {
    if (challenging) return;
    const stakeVal = Math.max(0, parseInt(stake, 10) || 0);
    setChallenging(true);
    try {
      const { data } = await api.post('/battles/challenge', {
        opponent_qr_token: scanned.token,
        stake_amount: stakeVal,
      });
      toast.success(t('scan.challengeSent'));
      // Wait for opponent to accept — take challenger to a waiting screen: battles list
      nav(`/battles?pending=${data.challenge_id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    } finally { setChallenging(false); }
  };

  const target = scanned?.user;
  const level = target ? getLevelForAura(target.aura_points || 0) : null;

  return (
    <div className="min-h-screen bg-aura-bg relative overflow-hidden">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />
      <header className="relative flex items-center justify-between px-5 pt-6">
        <button onClick={() => nav(-1)} className="text-aura-textDim hover:text-white flex items-center gap-1" data-testid="btn-back">
          <ChevronLeft size={20} /> {t('common.back')}
        </button>
        <span className="font-display font-black tracking-[0.3em] aura-text-gradient text-sm">SCAN</span>
        <span className="w-8" />
      </header>

      <main className="relative mx-auto max-w-md px-5 pt-4">
        {!scanned && !error && (
          <>
            <div className="text-center mb-4">
              <ScanLine size={32} className="text-aura-cyan mx-auto animate-aura-pulse" />
              <div className="text-white font-display font-bold text-xl mt-2">{t('scan.title')}</div>
              <div className="text-aura-textDim text-xs">{t('scan.subtitle')}</div>
            </div>
            <div id="qr-region" className="w-full aspect-square rounded-2xl overflow-hidden bg-black border border-aura-cyan/40 shadow-neon-cyan" data-testid="qr-region" />
          </>
        )}

        {error && (
          <div className="aura-card p-6 text-center mt-8" data-testid="scan-error">
            <div className="text-4xl mb-2">⚠️</div>
            <div className="text-white font-display font-bold">{error}</div>
            <button onClick={() => { setError(null); setScanned(null); }} className="aura-btn-ghost mt-4">{t('common.retry')}</button>
          </div>
        )}

        {scanned && target && (
          <div className="text-center pt-4" data-testid="scan-result">
            <div className="text-[10px] uppercase tracking-[0.3em] text-aura-textDim mb-3">{t('scan.youScanned')}</div>
            <UserAvatar user={target} size="xl" className="mx-auto" />
            <div className="mt-3 font-display font-black text-2xl text-white">{target.name}</div>
            <div className="text-aura-textDim text-sm">@{target.username}</div>
            <div className="mt-3 flex items-center justify-center gap-3">
              <AuraScore points={target.aura_points || 0} size="md" />
            </div>
            {level && <div className="mt-2 font-display font-black tracking-widest" style={{ color: level.color }}>{t(level.key)}</div>}

            <div className="mt-8 space-y-3">
              {scanned.can_challenge && (
                <div className="aura-card p-4 text-left" data-testid="stake-panel">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black text-aura-gold">APUESTA (STAKE)</div>
                    <div className="text-[10px] font-mono text-aura-textDim">
                      Saldo: <span className="text-aura-cyan font-black">{myBalance.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="stake-input"
                      type="number" min="0" step="10" max={myBalance}
                      value={stake}
                      onChange={(e) => setStake(e.target.value)}
                      className="flex-1 bg-aura-surface2 border border-aura-gold/30 rounded-xl px-3 py-2 text-lg text-white font-mono font-black outline-none focus:border-aura-gold"
                    />
                    <span className="text-aura-gold font-display font-black">AURA</span>
                  </div>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {[0, 50, 100, 500, 1000].filter((v) => v <= myBalance).map((v) => (
                      <button key={v} type="button" data-testid={`stake-quick-${v}`} onClick={() => setStake(v)}
                        className={`text-[10px] px-2 py-1 rounded-full border font-mono font-bold transition ${Number(stake) === v ? 'bg-aura-gold text-black border-aura-gold' : 'border-white/10 text-white/70 hover:border-aura-gold/60'}`}>
                        {v === 0 ? 'sin apuesta' : v.toLocaleString()}
                      </button>
                    ))}
                  </div>
                  {Number(stake) > 0 && (
                    <div className="mt-2 text-[10px] text-aura-textDim">
                      Pot potencial: <span className="text-aura-gold font-mono font-black">{(Number(stake) * 2).toLocaleString()} AURA</span> · el ganador se lo lleva todo
                    </div>
                  )}
                </div>
              )}

              {scanned.can_challenge ? (
                <button onClick={challenge} disabled={challenging || Number(stake) > myBalance} data-testid="btn-challenge" className="aura-btn-primary w-full h-14 text-base">
                  ⚔️ {challenging ? t('common.loading') : `${t('scan.challenge')}${Number(stake) > 0 ? ` · ${Number(stake).toLocaleString()}` : ''}`}
                </button>
              ) : (
                <div className="aura-card p-4 text-sm text-aura-textDim">
                  {scanned.reason === 'closed' && t('scan.reasonClosed')}
                  {scanned.reason === 'self' && t('scan.reasonSelf')}
                  {scanned.reason === 'followers_only' && t('scan.reasonFollowers')}
                  {scanned.reason === 'following_only' && t('scan.reasonFollowing')}
                </div>
              )}
              {scanned.reason !== 'self' && (
                <button onClick={() => nav(`/recognize/${scanned.token}`)} data-testid="btn-recognize" className="aura-btn-ghost w-full h-12 border-aura-cyan/40">
                  ⚡ Otorgar Aura
                </button>
              )}
              <button onClick={() => setScanned(null)} className="aura-btn-ghost w-full">{t('scan.scanAgain')}</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
